import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const steps = [
  {
    number: '01',
    icon: 'ri-shopping-cart-line',
    title: 'Purchase an SEO Power Plan',
    description: 'Go to the purchase page, select a SEOPower plan, and complete checkout. Your license key will arrive within 24 hours.',
    tips: ['Multiple plans available', 'Secure checkout', 'License key delivered by email'],
  },
  {
    number: '02',
    icon: 'ri-user-add-line',
    title: 'Enter Your License Key',
    description: 'Select the Run SEO Report button in the upper right of the webpage. When the SEOpower app loads, enter your license key to get started.',
    tips: ['Secure login', 'License key validation', 'Tracks license key use'],
  },
  {
    number: '03',
    icon: 'ri-links-line',
    title: 'Enter Your Page URL',
    description: 'Enter your URL into the SEOPower URL field. Our engine immediately begins crawling and analyzing any active domain webpage structures, contents, and metadata. HINT: Before running SEOPower, check to ensure your target URL is returning content. Active URLs without content are valid but will not be analyzed properly. This may occur due to many reasons including privacy settings and maintenance.',
    tips: ['Supports any public URL', 'Works on landing pages, blogs, product pages', 'Analyzes competitor pages'],
  },
  {
    number: '04',
    icon: 'ri-bar-chart-2-line',
    title: 'Review Your SEO Score',
    description: 'Get an instant overall SEO health score with a detailed breakdown across key categories: on-page content, technical SEO, keyword usage, and readability. Right mouse click to print or save your report to a PDF.',
    tips: ['Score from 0–100', 'Category-level insights', 'Benchmark against competitors'],
  },
  {
    number: '05',
    icon: 'ri-lightbulb-flash-line',
    title: 'Act on Recommendations',
    description: 'SEOPower delivers prioritized, business-driven recommendations. Each suggestion is ranked by impact so you always know what to fix for maximum results.',
    tips: ['Prioritized by impact', 'Step-by-step fix instructions', 'Technical and Business summaries'],
  },
  {
    number: '06',
    icon: 'ri-refresh-line',
    title: 'Track & Re-Analyze',
    description: 'After making changes, re-run the analysis 30-90 days later to measure your improvement. Run and monitor your score over time to keep your pages optimized as search algorithms and competitors evolve.',
    tips: ['Unlimited re-analyses', 'Progressive analysis', 'Fine tune content'],
  },
];

const faqs = [
  {
    question: 'How often should I re-analyze my pages?',
    answer: 'We recommend re-analyzing after every significant content update or at least every 30 - 90 days. Search engine algorithms change frequently, new competitors enter, and regular analysis ensures your pages stay competitive.',
  },
  {
    question: 'Can I analyze competitor pages?',
    answer: 'Yes! Simply paste any publicly accessible URL into SEOPower. Analyzing competitor pages is a great way to discover gaps in your own strategy and find new keyword opportunities.',
  },
  {
    question: 'What types of pages can SEOPower analyze?',
    answer: 'SEOPower works on any public web page — landing pages, blog posts, product pages, homepages, and more. As long as the URL is active and publicly accessible, our SEO engine can analyze it.',
  },
  {
    question: 'How is SEOPower different from other SEO tools?',
    answer: 'SEOPower uses proprietary AI to deliver business-driven recommendations, not just generic checklists that overload you with quantity of stuff. Every suggestion is tied to real traffic and prioritized ranking impacts so you can act on what actually improves your SEO ranking.',
  },
  {
    question: 'Do I need technical SEO knowledge to use SEOPower?',
    answer: 'Not at all. SEOPower is designed for marketers, business owners, and content creators. All recommendations are written in plain language with clear, actionable steps. We do recommend that our Technical SEO section be shared with your content architects or technical leads.',
  },
];

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Use SEOPower for AI-Powered SEO Analysis',
  description: 'A step-by-step guide to analyzing and optimizing your web pages with SEOPower\'s AI-driven SEO tool.',
  url: `${siteUrl}/guide`,
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Purchase an SEO Power Plan',
      text: 'Go to the purchase page, select a SEOPower plan, and complete checkout. Your license key will arrive within 24 hours.',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Enter Your License Key',
      text: 'Select the Run SEO Report button in the upper right of the webpage. When the SEOpower app loads, enter your license key to get started.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Enter Your Page URL',
      text: 'Enter your URL into the SEOPower URL field. Our engine immediately begins crawling and analyzing any active domain webpage structures, contents, and metadata.',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Review Your SEO Score',
      text: 'Get an instant overall SEO health score with a detailed breakdown across key categories: on-page content, technical SEO, keyword usage, and readability.',
    },
    {
      '@type': 'HowToStep',
      position: 5,
      name: 'Act on Recommendations',
      text: 'SEOPower delivers prioritized, business-driven recommendations. Each suggestion is ranked by impact so you always know what to fix for maximum results.',
    },
    {
      '@type': 'HowToStep',
      position: 6,
      name: 'Track & Re-Analyze',
      text: 'After making changes, re-run the analysis 30-90 days later to measure your improvement and keep your pages optimized.',
    },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How often should I re-analyze my pages?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We recommend re-analyzing after every significant content update or at least every 30 - 90 days. Search engine algorithms change frequently, new competitors enter, and regular analysis ensures your pages stay competitive.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I analyze competitor pages?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Simply paste any publicly accessible URL into SEOPower. Analyzing competitor pages is a great way to discover gaps in your own strategy and find new keyword opportunities.',
      },
    },
    {
      '@type': 'Question',
      name: 'What types of pages can SEOPower analyze?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'SEOPower works on any public web page — landing pages, blog posts, product pages, homepages, and more. As long as the URL is active and publicly accessible, our SEO engine can analyze it.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is SEOPower different from other SEO tools?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'SEOPower uses proprietary AI to deliver business-driven recommendations, not just generic checklists. Every suggestion is tied to real traffic and prioritized ranking impacts so you can act on what actually improves your SEO ranking.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need technical SEO knowledge to use SEOPower?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Not at all. SEOPower is designed for marketers, business owners, and content creators. All recommendations are written in plain language with clear, actionable steps.',
      },
    },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Guide',
      item: `${siteUrl}/guide`,
    },
  ],
};

const GuidePage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <title>SEO Guide - How to Use SEOPower for Better Search Rankings</title>
      <meta name="description" content="Follow our step-by-step SEO guide to analyze your pages, boost search rankings, and grow organic traffic using SEOPower's AI-powered optimization tool." />
      <meta name="keywords" content="SEO guide, how to use SEOPower, SEO analysis steps, improve search rankings, SEO optimization tutorial" />
      <link rel="canonical" href={`${siteUrl}/guide`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede9fe] to-white"></div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Your Complete Guide to<br />
            <span className="text-[#4f3af6]">SEOPower</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Everything you need to know to start optimizing your pages, boosting your rankings, and growing organic traffic — step by step.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-500">Follow these six steps to get the most out of SEOPower.</p>
          </div>
          <div className="space-y-10">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex gap-8 items-start bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center rounded-2xl bg-[#ede9fe]">
                  <i className={`${step.icon} text-2xl text-[#4f3af6]`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-bold text-[#4f3af6] tracking-widest uppercase">{step.number}</span>
                    <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">{step.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {step.tips.map((tip, i) => (
                      <span key={i} className="px-3 py-1 bg-[#f5f3ff] text-[#4f3af6] text-sm rounded-full font-medium">
                        <i className="ri-check-line mr-1"></i>{tip}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Banner */}
      <section className="py-16 bg-[#ede9fe]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: 'ri-speed-up-line', title: 'Real-Time Analysis', desc: 'Results delivered in seconds, not hours.' },
              { icon: 'ri-money-dollar-circle-line', title: 'Business-Driven Insights', desc: 'To drive immediate improvements.' },
              { icon: 'ri-shield-check-line', title: 'Always Up to Date', desc: 'Our analysis is always real time and current.' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#4f3af6]/10">
                  <i className={`${item.icon} text-2xl text-[#4f3af6]`}></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-500">Quick answers to common questions about using SEOPower.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-semibold text-gray-900 text-base">{faq.question}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-4">
                    <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-[#4f3af6] text-lg`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed text-sm border-t border-gray-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-[#ede9fe] to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Boost Your Rankings?</h2>
          <p className="text-lg text-gray-600 mb-8">Start your first analysis today and see exactly what's holding your pages back.</p>
          <Link
            to="/purchase"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#4f3af6] text-white rounded-full font-medium hover:bg-[#4f3af6]/90 transition-all whitespace-nowrap cursor-pointer"
          >
            Try SEOPower Today
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuidePage;
